import { ChevronDown, ChevronRight, History, LoaderCircle, SlidersHorizontal } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  apiClient,
  type AuditOption,
  type ConfigAuditEntry,
  type ConfigAuditPage as Pagina
} from "@/shared/api/api-client";
import { SearchSelect } from "@/shared/components/SearchSelect";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { auditAuthorLabel, auditAuthorTitle, automaticAuthorLabel } from "./audit-author";
import { LoginFailuresCard } from "./LoginFailuresCard";

import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { Pager } from "@/shared/components/Pager";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { Select } from "@/shared/components/Select";
import { periodoDaConsulta } from "@/shared/utils/instant-period";
import { promptTaskLabel } from "@/shared/vocabulary/prompt-tasks";
/**
 * Administração → Auditoria.
 *
 * A trilha era gravada desde agosto e nunca foi legível — só por SQL. Com
 * as 13 abas de Ajustes passando a registrar, esta tela é onde se
 * pergunta "por que essa nota mudou?" e "quem desativou esse veículo?".
 *
 * **A data é convertida de fuso**, ao contrário da janela de menções:
 * `changed_at` é carimbo de VERDADE (o instante em que alguém salvou),
 * não hora de parede de terceiro. Ver `parseServerTime`.
 */

const ROTULO_TABELA: Record<string, string> = {
  brand: "Marcas",
  channel: "Canais",
  competitor: "Concorrentes",
  key_message: "Mensagens-chave",
  publisher_group: "Grupos de publicadores",
  publisher_person: "Publicadores",
  publisher_tier: "Porte do publicador",
  spokesperson: "Porta-vozes",
  theme: "Subtemas",
  theme_group: "Temas",
  vehicle: "Veículos",
  company: "Dados da empresa",
  prompt_model: "Modelo por tarefa",
  saved_filter: "Filtros salvos",
  export_column_preset: "Planilhas salvas",
  score_config: "Pesos do CENA",
  format_score: "Pontos de formato",
  approach_score: "Pontos de abordagem",
  vehicle_score: "Pontos de veículo",
  channel_score: "Pontos de canal",
  ai_provider_credential: "Credenciais de IA",
  ai_model: "Modelos de IA",
  copiloto_setting: "Configuração da Mestra Help",
  catalog_import: "Importação de catálogo",
  upload_template: "Templates de upload",
  prompt_default: "Prompts padrão",
  prompt: "Textos de prompt da empresa",
  prompt_rule: "Premissas",
  classification_term: "Termos de classificação",
  brand_focus_term: "Termos de foco",
  reputaition_user: "Cadastro de usuário",
  company_group: "Núcleo",
  versa_ai_setting: "IA do Versa",
  versa_sentinel_alert: "Sentinela — alertas",
  versa_sentinel_profile: "Sentinela — instruções da IA",
  versa_sentinel_export: "Sentinela — pedido para IA de fora",
  versa_short_link: "Links curtos",
  versa_template: "Versa — modelos de relatório"
};

/** As seções de acesso (2026-10-23, proposta de IAM §8.2): a linha diz em
 *  QUEM a mudança caiu, e os campos têm nome em português. Nas outras
 *  seções o campo segue como sempre (a chave, com espaço no lugar de `_`). */
const SECOES_DE_ACESSO = new Set(["reputaition_user", "company_group"]);

const ROTULO_CAMPO_DE_ACESSO: Record<string, string> = {
  name: "nome",
  email: "e-mail",
  description: "descrição",
  role: "cargo",
  status: "situação",
  scope: "escopo",
  groups: "núcleos",
  companies: "empresas",
  suspension: "suspensão",
  login_method: "forma de entrar",
  login_exception_until: "exceção de senha até",
  password_reset: "redefinição de senha",
  password_email: "e-mail de senha",
  welcome_email: "e-mail de boas-vindas",
  login_lockout: "bloqueio de login"
};

const ROTULO_ACAO: Record<string, string> = {
  create: "Criou",
  update: "Alterou",
  delete: "Removeu"
};

/** Os filtros da tela. As datas são `AAAA-MM-DD` do `<input type=date>`,
 *  no fuso de quem olha — a conversão pra instante vai em `periodoDaConsulta` (shared/utils/instant-period). */
type Filtros = {
  table_name: string;
  action: string;
  only_global: boolean;
  company_id: string;
  changed_by: string;
  de: string;
  ate: string;
  page: number;
};

const SEM_FILTRO: Filtros = {
  table_name: "",
  action: "",
  only_global: false,
  company_id: "",
  changed_by: "",
  de: "",
  ate: "",
  page: 1
};

function rotuloDaOpcao(opcao: AuditOption): string {
  return opcao.name || automaticAuthorLabel(opcao.id) || opcao.id;
}

/** `null` vira travessão; o resto vira texto legível. */
function valor(bruto: unknown): string {
  if (bruto === null || bruto === undefined) return "—";
  if (typeof bruto === "boolean") return bruto ? "sim" : "não";
  if (typeof bruto === "object") return JSON.stringify(bruto);
  return String(bruto);
}

function nomeDoCampo(campo: string, tabela: string): string {
  if (SECOES_DE_ACESSO.has(tabela) && ROTULO_CAMPO_DE_ACESSO[campo]) return ROTULO_CAMPO_DE_ACESSO[campo];
  return campo.replace(/_/g, " ");
}

/** A chave do diff que diz de qual consulta da IA ("Pedir melhorias à IA")
 *  a mudança veio. Viaja DENTRO do diff, na forma `{old, new}` de todo
 *  campo (`old` sempre nulo), mas não é campo do cadastro: sai da lista de
 *  campos e vira o link "via consultoria #N". */
const CHAVE_CONSULTORIA = "rule_review_id";

function consultoriaDaMudanca(diff: ConfigAuditEntry["diff"]): number | null {
  const id = diff[CHAVE_CONSULTORIA]?.new;
  return typeof id === "number" && Number.isInteger(id) && id > 0 ? id : null;
}

/** A identidade da linha de prompt (`tarefa/fonte/posição`). A linha
 *  efetiva da empresa não tem id estável (é o geral ou a personalização),
 *  então o servidor a grava no diff como `line` — mas isso diz QUAL linha
 *  mudou, não é campo alterado: sai da contagem e vira "Linha: …". */
const CHAVE_LINHA = "line";
const ROTULO_FONTE: Record<string, string> = { press: "imprensa", network: "redes" };

function linhaDoPrompt(entrada: ConfigAuditEntry): string | null {
  if (entrada.table_name !== "prompt") return null;
  const bruto = entrada.diff[CHAVE_LINHA]?.new;
  if (typeof bruto !== "string") return null;
  const [tarefa, fonte, posicao] = bruto.split("/");
  if (!tarefa || !fonte || posicao === undefined) return bruto;
  return `${promptTaskLabel(tarefa)} · ${ROTULO_FONTE[fonte] ?? fonte} · posição ${posicao}`;
}

function LinhaDaTrilha({ entrada }: { entrada: ConfigAuditEntry }) {
  const [aberta, setAberta] = useState(false);
  const linha = linhaDoPrompt(entrada);
  const campos = Object.entries(entrada.diff).filter(
    ([campo]) => campo !== CHAVE_CONSULTORIA && !(linha !== null && campo === CHAVE_LINHA)
  );
  const consultoria = consultoriaDaMudanca(entrada.diff);
  // `parseServerTime` devolve MILISSEGUNDOS, não `Date` — e converte
  // o fuso, que é o certo aqui: `changed_at` é carimbo de verdade (o
  // instante em que alguém salvou), não hora de parede da clipadora.
  const quando = parseServerTime(entrada.changed_at);

  return (
    <>
      <tr>
        <td>
          <button
            className="audit-toggle"
            type="button"
            onClick={() => setAberta(!aberta)}
            aria-expanded={aberta}
          >
            {aberta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {quando === null
              ? entrada.changed_at
              : new Date(quando).toLocaleString("pt-BR")}
          </button>
        </td>
        <td>
          {ROTULO_TABELA[entrada.table_name] ?? entrada.table_name}
          {entrada.record_label && (
            <>
              {" · "}
              <strong className="audit-record">{entrada.record_label}</strong>
            </>
          )}
        </td>
        <td>
          {entrada.company_name ?? (
            <span className="audit-global">Global da instalação</span>
          )}
        </td>
        <td>{ROTULO_ACAO[entrada.action] ?? entrada.action}</td>
        <td title={auditAuthorTitle(entrada)}>{auditAuthorLabel(entrada)}</td>
        <td className="audit-fields">
          {campos.length === 1
            ? nomeDoCampo(campos[0][0], entrada.table_name)
            : `${campos.length} campos`}
          {linha !== null && ` · Linha: ${linha}`}
          {consultoria !== null &&
            (entrada.company_id ? (
              <>
                {" · "}
                <Link
                  className="audit-origin"
                  to={`/empresas/${encodeURIComponent(entrada.company_id)}/justificativas?consultoria=${consultoria}`}
                >
                  via consultoria #{consultoria}
                </Link>
              </>
            ) : (
              ` · via consultoria #${consultoria}`
            ))}
        </td>
      </tr>
      {aberta && (
        <tr className="audit-detail-row">
          <td colSpan={6}>
            <table className="audit-detail">
              <tbody>
                {campos.map(([campo, mudanca]) => (
                  <tr key={campo}>
                    <th>{nomeDoCampo(campo, entrada.table_name)}</th>
                    <td className="audit-old">{valor(mudanca.old)}</td>
                    <td aria-hidden="true">→</td>
                    <td className="audit-new">{valor(mudanca.new)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export function ConfigAuditPage({ token }: { token: string }) {
  const [pagina, setPagina] = useState<Pagina | null>(null);
  const [tabelas, setTabelas] = useState<string[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  // A falha das seções tem estado PRÓPRIO: engolida (`setTabelas([])`), o
  // filtro ficava só com "Todas" e era indistinguível de uma instalação
  // sem seção auditada (2026-10-09, teste de render). O contador refaz a
  // consulta no "Tentar de novo".
  const [erroTabelas, setErroTabelas] = useState<string | null>(null);
  const [tentativaTabelas, setTentativaTabelas] = useState(0);
  // Descarte por CARGA: trocar o filtro duas vezes com a primeira resposta
  // chegando por último deixava na tela as linhas do filtro ANTERIOR sob o
  // filtro novo marcado. Só a consulta mais recente escreve (2026-10-09,
  // teste de render).
  const cargaAtual = useRef(0);
  const [filtros, setFiltros] = useState<Filtros>(SEM_FILTRO);
  // As opções de "Quem" vêm de uma vez (só quem aparece na trilha); as
  // de "Empresa" vêm com busca no servidor — a instalação tem milhares.
  const [autores, setAutores] = useState<AuditOption[]>([]);
  const [empresas, setEmpresas] = useState<AuditOption[]>([]);
  // O nome de toda empresa que já passou pela caixa (lista inicial e
  // cada busca), pelo id: a escolhida numa BUSCA pode não estar na lista
  // inicial, e sem isto a caixa mostraria o código.
  const nomesDeEmpresa = useRef(new Map<string, string>());
  const lembrar = (lista: AuditOption[]) => {
    for (const opcao of lista) nomesDeEmpresa.current.set(opcao.id, rotuloDaOpcao(opcao));
    return lista;
  };

  useEffect(() => {
    apiClient.listAuditAuthors(token).then(setAutores).catch(() => setAutores([]));
    apiClient
      .listAuditCompanies(token)
      .then(lista => setEmpresas(lembrar(lista)))
      .catch(() => setEmpresas([]));
  }, [token]);

  const buscarEmpresa = useCallback(
    async (q: string) =>
      lembrar(await apiClient.listAuditCompanies(token, q)).map(opcao => ({
        value: opcao.id,
        label: rotuloDaOpcao(opcao)
      })),
    [token]
  );

  const filtrando =
    filtros.table_name !== "" ||
    filtros.action !== "" ||
    filtros.only_global ||
    filtros.company_id !== "" ||
    filtros.changed_by !== "" ||
    filtros.de !== "" ||
    filtros.ate !== "";
  const periodoInvertido = filtros.de !== "" && filtros.ate !== "" && filtros.de > filtros.ate;

  useEffect(() => {
    setErroTabelas(null);
    apiClient
      .listAuditedTables(token)
      .then(setTabelas)
      .catch(error =>
        setErroTabelas(errorMessage(error, "Não foi possível carregar as seções."))
      );
  }, [token, tentativaTabelas]);

  useEffect(() => {
    const carga = ++cargaAtual.current;
    setCarregando(true);
    apiClient
      .listAuditLog(token, {
        table_name: filtros.table_name || undefined,
        action: filtros.action || undefined,
        only_global: filtros.only_global,
        company_id: filtros.only_global ? undefined : filtros.company_id || undefined,
        changed_by: filtros.changed_by || undefined,
        ...periodoDaConsulta(filtros.de, filtros.ate),
        page: filtros.page,
        size: 50
      })
      .then(resultado => {
        if (carga !== cargaAtual.current) return;
        setPagina(resultado);
        setErro(null);
      })
      .catch(error => {
        if (carga !== cargaAtual.current) return;
        setErro(errorMessage(error, "Não foi possível carregar."));
      })
      .finally(() => {
        if (carga === cargaAtual.current) setCarregando(false);
      });
  }, [token, filtros]);

  return (
    <div className="copiloto-admin ajustes-panel admin-panel">
      <AdminPageHeader title="Auditoria" description="Quem mudou o quê na configuração, e quando." />

      <div className="card audit-filters">
        <label className="field">
          <span>Seção</span>
          <Select
            value={filtros.table_name}
            onChange={event =>
              setFiltros({ ...filtros, table_name: event.target.value, page: 1 })
            }
          >
            <option value="">Todas</option>
            {tabelas.map(tabela => (
              <option key={tabela} value={tabela}>
                {ROTULO_TABELA[tabela] ?? tabela}
              </option>
            ))}
          </Select>
        </label>
        <label className="field">
          <span>Ação</span>
          <Select
            value={filtros.action}
            onChange={event =>
              setFiltros({ ...filtros, action: event.target.value, page: 1 })
            }
          >
            <option value="">Todas</option>
            <option value="create">Criou</option>
            <option value="update">Alterou</option>
            <option value="delete">Removeu</option>
          </Select>
        </label>
        <div className="audit-filter-pick">
          <SearchSelect
            label="Empresa"
            value={filtros.company_id}
            valueLabel={nomesDeEmpresa.current.get(filtros.company_id)}
            options={empresas.map(opcao => ({ value: opcao.id, label: rotuloDaOpcao(opcao) }))}
            search={buscarEmpresa}
            allLabel="Todas"
            disabled={filtros.only_global}
            disabledReason="Configurações da instalação não têm empresa."
            onChange={id => setFiltros({ ...filtros, company_id: id, page: 1 })}
          />
        </div>
        <div className="audit-filter-pick">
          <SearchSelect
            label="Quem"
            value={filtros.changed_by}
            options={autores.map(opcao => ({ value: opcao.id, label: rotuloDaOpcao(opcao) }))}
            allLabel="Todos"
            onChange={id => setFiltros({ ...filtros, changed_by: id, page: 1 })}
          />
        </div>
        <label className="field audit-date">
          <span>De</span>
          <input
            type="date"
            value={filtros.de}
            max={filtros.ate || undefined}
            onChange={event => setFiltros({ ...filtros, de: event.target.value, page: 1 })}
          />
        </label>
        <label className="field audit-date">
          <span>Até</span>
          <input
            type="date"
            value={filtros.ate}
            min={filtros.de || undefined}
            onChange={event => setFiltros({ ...filtros, ate: event.target.value, page: 1 })}
          />
        </label>
        <label className="audit-check">
          <input
            type="checkbox"
            checked={filtros.only_global}
            onChange={event =>
              setFiltros({
                ...filtros,
                only_global: event.target.checked,
                // Instalação não tem empresa: os dois filtros se excluem.
                company_id: event.target.checked ? "" : filtros.company_id,
                page: 1
              })
            }
          />
          <span>Só configurações da instalação</span>
        </label>
        {filtrando && (
          <button
            type="button"
            className="ghost-button audit-clear"
            onClick={() => setFiltros(SEM_FILTRO)}
          >
            <SlidersHorizontal size={14} aria-hidden="true" />
            Limpar filtros
          </button>
        )}
      </div>
      {periodoInvertido && (
        <p className="form-error" role="alert">
          A data inicial é depois da final — nenhuma mudança cabe nesse período.
        </p>
      )}

      {erroTabelas && (
        <ApiState kind="error" onRetry={() => setTentativaTabelas(n => n + 1)}>
          <span role="alert">{erroTabelas}</span>
        </ApiState>
      )}

      {erro && (
        <p className="form-error" role="alert">
          {erro}
        </p>
      )}

      <div className="card">
        <div className="card-title">
          <div>
            <History size={18} aria-hidden="true" />
            <h3>Mudanças</h3>
            <p>
              {pagina
                ? `${pagina.total} registro${pagina.total === 1 ? "" : "s"}`
                : "Carregando…"}
            </p>
          </div>
          {carregando && <LoaderCircle className="spin" size={16} />}
        </div>

        {pagina && pagina.items.length === 0 ? (
          <p className="copiloto-empty-list">
            Nenhuma mudança registrada com esse recorte.
          </p>
        ) : (
          <table className="ai-admin-table audit-table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Seção</th>
                <th>Empresa</th>
                <th>Ação</th>
                <th>Quem</th>
                <th>Mudou</th>
              </tr>
            </thead>
            <tbody>
              {pagina?.items.map(entrada => (
                <LinhaDaTrilha key={entrada.id} entrada={entrada} />
              ))}
            </tbody>
          </table>
        )}

        {pagina && (
          <Pager
            page={pagina.page}
            size={pagina.size}
            count={{ total: pagina.total }}
            onPage={proxima => setFiltros({ ...filtros, page: proxima })}
          />
        )}
      </div>

      {/* Proposta de IAM §8.2 (2026-10-23): as tentativas de login
          malsucedidas, com o mesmo período da página. */}
      <LoginFailuresCard token={token} de={filtros.de} ate={filtros.ate} />
    </div>
  );
}
